<template>
  <div class="game-container">
    <div v-if="!gameStarted" class="start-screen">
      <h1>Găsiți diferențele!</h1>
      <div class="difficulty-select">
        <button
            v-for="level in difficultyLevels"
            :key="level.id"
            @click="startGame(level)"
            :class="['difficulty-btn', level.id]"
        >
          {{ level.name }}
        </button>
      </div>
      <div class="instructions">
        <h3>Cum se joacă:</h3>
        <p>🔍 Găsiți toate diferențele dintre imagini</p>
        <p>⏱️ Bate cronometrul</p>
        <p>🎯 Mai puține încercări = scor mai bun</p>
      </div>
    </div>

    <div v-else>
      <div class="game-header">
        <div class="stats">
          <div class="stat-item">
            ⏳ Timp: {{ formattedTime }}
          </div>
          <div class="stat-item">
            🎯 Încercări: {{ attempts }}
          </div>
          <div class="stat-item">
            ⭐ Scor: {{ score }}
          </div>
          <div class="stat-item">
            🔍 Diferențe rămase: {{ totalDifferences - foundDifferences.length }}
          </div>
          <div class="stat-item">
            <button
                @click="cancelGame"
                class="cancel-btn"
                title="Încearcă din nou"
            >
              Încearcă din nou
            </button>
          </div>
        </div>
      </div>

      <div class="game-content">
        <div class="images-wrapper">
          <!-- Original Image -->
          <div class="image-container">
            <img :src="currentImage" alt="Original" class="game-image">
            <div class="label">Original</div>
          </div>

          <!-- Modified Image -->
          <div class="image-container" @click="checkClick">
            <img :src="currentImage" alt="Modified" class="game-image">
            <div class="label">Găsiți diferențele</div>

            <!-- Differences Layer -->
            <div class="difference-layer">
              <div
                  v-for="(diff, index) in differences"
                  :key="index"
                  class="difference"
                  :class="diff.type"
                  :style="getDifferenceStyle(diff)"
                  @click.stop=""
              ></div>
            </div>

            <!-- Click Effects -->
            <div
                v-for="(mark, index) in clickMarks"
                :key="'mark-' + index"
                class="click-mark"
                :style="mark.style"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Win/Lose Screens -->
    <div v-if="gameEnded" class="end-screen">
      <div v-if="gameWon" class="win-screen">
        <h2>🎉 Felicitări! 🎉</h2>
        <div class="final-stats">
          <p>Scor final: {{ scor }}</p>
          <p>Timp rămas: {{ formattedTime }}</p>
          <p>Numărul total de încercări: {{ încercări }}</p>
        </div>
        <button @click="startGame(currentDifficulty)" class="play-again-btn">
          Joacă din nou
        </button>
      </div>

      <div v-else class="lose-screen">
        <h2>😞 Timpul a expirat! 😞</h2>
        <p>Ați găsit {{ foundDifferences.length }} din {{ totalDifferences }} diferențe</p>
        <button @click="startGame(currentDifficulty)" class="play-again-btn">
          Încearcă din nou
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      // Game Configuration
      difficultyLevels: [
        {
          id: 'easy',
          name: 'Începător',
          differences: 3,
          time: 120,
          dotSize: 12,
          types: ['dot'],
          scoreMultiplier: 1
        },
        {
          id: 'medium',
          name: 'Avansat',
          differences: 5,
          time: 90,
          dotSize: 8,
          types: ['dot', 'circle'],
          scoreMultiplier: 1.5
        },
        {
          id: 'hard',
          name: 'Expert',
          differences: 7,
          time: 60,
          dotSize: 6,
          types: ['dot', 'circle', 'square'],
          scoreMultiplier: 2
        }
      ],

      // Game State
      gameStarted: false,
      gameEnded: false,
      gameWon: false,
      currentDifficulty: null,
      currentImage: '',
      differences: [],
      foundDifferences: [],
      clickMarks: [],
      attempts: 0,
      timeLeft: 0,
      timer: null,
      audio: {
        click: new Audio('/assets/sounds/click.mp3'),
        success: new Audio('/assets/sounds/success.wav'),
        error: new Audio('/assets/sounds/error.wav'),
        win: new Audio('/assets/sounds/win.wav'),
        lose: new Audio('/assets/sounds/lose.wav')
      }
    }
  },
  computed: {
    totalDifferences() {
      return this.differences.length
    },
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60)
      const seconds = this.timeLeft % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    },
    score() {
      const timeBonus = this.timeLeft * 10;
      const accuracy = Math.max(0, 1 - (this.attempts / 100));
      const difficultyBonus = this.currentDifficulty.scoreMultiplier * 100
      return Math.floor(
          (this.foundDifferences.length * 500) +
          timeBonus +
          (difficultyBonus * accuracy))
    }
  },
  methods: {
    async startGame(difficulty) {
      this.gameStarted = true
      this.gameEnded = false
      this.gameWon = false
      this.currentDifficulty = difficulty
      this.attempts = 0
      this.timeLeft = difficulty.time
      this.foundDifferences = []
      this.clickMarks = []

      // Load random image
      const images = [
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/71415/conversions/erpsync-main-image-details.jpg",
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/98423-ro/conversions/erpsync-main-image-details.jpg",
        "https://s3-eu-west-1.amazonaws.com/a10ro/Lot/76485/conversions/erpsync-main-image-details.jpg"
      ]
      this.currentImage = images[Math.floor(Math.random() * images.length)]

      // Generate differences
      this.differences = Array.from({ length: difficulty.differences }, () => ({
        x: Math.random() * 90 + 5,
        y: Math.random() * 90 + 5,
        type: difficulty.types[Math.floor(Math.random() * difficulty.types.length)],
        size: difficulty.dotSize
      }))

      // Start timer
      this.timer = setInterval(() => {
        this.timeLeft--
        if (this.timeLeft <= 0) {
          this.endGame(false)
        }
      }, 1000)
    },
    checkClick(event) {
      const rect = event.currentTarget.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const clickY = event.clientY - rect.top
      const clickTime = Date.now()

      // Add click mark
      this.clickMarks.push({
        style: {
          left: `${(clickX / rect.width) * 100}%`,
          top: `${(clickY / rect.height) * 100}%`
        },
        timestamp: clickTime
      })

      // Check differences
      let found = false
      this.differences.forEach((diff, index) => {
        if (this.foundDifferences.includes(index)) return

        const diffX = (diff.x / 100) * rect.width
        const diffY = (diff.y / 100) * rect.height
        const tolerance = diff.size * 3

        const distance = Math.sqrt(
            Math.pow(clickX - diffX, 2) +
            Math.pow(clickY - diffY, 2))

        if (distance < tolerance) {
          found = true
          this.foundDifferences.push(index)
          this.audio.success.play()
          if (this.foundDifferences.length === this.totalDifferences) {
            this.endGame(true)
          }
        }
      })

      if (!found) {
        this.attempts++
        this.audio.error.play()
      } else {
        this.audio.click.play()
      }
    },
    getDifferenceStyle(diff) {
      const baseStyle = {
        left: `${diff.x}%`,
        top: `${diff.y}%`,
        width: `${diff.size}px`,
        height: `${diff.size}px`
      }

      switch(diff.type) {
        case 'circle':
          return { ...baseStyle, borderRadius: '50%', backgroundColor: '#ff0000' }
        case 'square':
          return { ...baseStyle, backgroundColor: '#00ff00', transform: 'rotate(45deg)' }
        default: // dot
          return { ...baseStyle, borderRadius: '50%', backgroundColor: '#fff' }
      }
    },
    endGame(won) {
      clearInterval(this.timer)
      this.gameEnded = true
      this.gameWon = won
      won ? this.audio.win.play() : this.audio.lose.play()
    },
    cancelGame() {
      if(confirm("Sigur doriți să anulați jocul?")) {
        clearInterval(this.timer)
        this.gameStarted = false
        this.gameEnded = false
        this.audio.click.play()
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.timer)
  }
}
</script>

<style scoped>
/* Base Styles */
.stat-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cancel-btn {
  margin-left: 8px;
  padding: 2px 6px;
  border: none;
  background: lightslategray;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: grey;
  transform: scale(1.1);
}

.cancel-btn:active {
  transform: scale(0.9);
}

.game-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  background: #f0f2f5;
}

.start-screen {
  text-align: center;
  padding: 40px;
}

.difficulty-select {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin: 30px 0;
}

.difficulty-btn {
  padding: 15px 30px;
  font-size: 1.2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.difficulty-btn:hover {
  transform: translateY(-2px);
}

.difficulty-btn.easy {
  background: #4CAF50;
  color: white;
}

.difficulty-btn.medium {
  background: #FFC107;
  color: black;
}

.difficulty-btn.hard {
  background: #F44336;
  color: white;
}

/* Game Header */
.game-header {
  margin-bottom: 20px;
}

.stats {
  display: flex;
  justify-content: center;
  gap: 30px;
  padding: 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.stat-item {
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Images Layout */
.images-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
}

.image-container {
  position: relative;
  cursor: crosshair;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.game-image {
  width: 100%;
  height: auto;
  display: block;
}

/* Differences */
.difference-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.difference {
  position: absolute;
  animation: pulse 1.5s infinite;
  transform-origin: center;
}

.dot {
  border-radius: 50%;
}

.click-mark {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 2px solid #ff0000;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: clickEffect 0.5s forwards;
}

/* End Screens */
.end-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 9999
}

.win-screen, .lose-screen {
  text-align: center;
  padding: 40px;
  background: #2c3e50;
  border-radius: 15px;
}

.play-again-btn {
  padding: 15px 40px;
  font-size: 1.2rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.play-again-btn:hover {
  transform: scale(1.05);
}

/* Animations */
@keyframes pulse {
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
}

@keyframes clickEffect {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
}
</style>